import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Helmet } from "react-helmet-async";
import classNames from "classnames";
import { v4 as uuid } from "uuid";
import {
  SearchBar,
  Pagination,
  EmptyLayout,
  Table,
} from "../../components/molecules";
import { selectInvitationsFilters } from "./index";
import {
  ROUTES,
  ONE,
  DEFAULT_LIMIT,
  EMPTY_ARRAY,
  CACHE_KEYS,
  EMPTY_STRING,
  SEARCH_DEBOUNCE_TIME,
  TWO,
} from "../../constants";
import { actions as userActions } from "./users.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { usersIcon } from "../../assets/icons";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { inviteUser } from "./users.actions";
import { selectAllInvitations } from "./users.selectors";
import { capitalizeFirstLetterOfWord, getFormattedDate } from "../../helpers";

const { setInvitationFilters } = userActions;
const { setSelectedModal } = modalActions;
const { FETCH_INVITATIONS } = SPINNER_NAMES;
const { USERROLES, USERS } = ROUTES;
const { MANAGE_USER_MODAL } = MODALS;
const ACCEPTED = "ACCEPTED";
const PENDING = "PENDING";
const EXPIRED = "EXPIRED";
const { INVITE_USER } = SPINNER_NAMES;
const { ORGANIZATION } = CACHE_KEYS;
const organization = JSON.parse(sessionStorage.getItem(ORGANIZATION));
const organizationId = organization?.id;

const Invitations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [invitationsCount, setInvitationsCount] = useState(null);
  const [pageCount, setPageCount] = useState(ONE);
  const filters = useSelector(selectInvitationsFilters);
  const invitationsData = useSelector(selectAllInvitations);

  const currentDate = new Date().toISOString();

  const getInvitations = useCallback(() => {
    dispatch(setInvitationFilters({ ...filters, organizationId, page: ONE }));
  }, [dispatch, filters]);

  useEffect(() => {
    if (pageCount < TWO) {
      setInvitationsCount(invitationsData.length);
      setPageCount(Math.ceil(invitationsCount / DEFAULT_LIMIT));
    }
  }, [invitationsCount, invitationsData.length, pageCount]);

  useEffect(
    () => () => {
      dispatch(setInvitationFilters());
    },
    [dispatch]
  );

  const handlePagination = useCallback(
    (value) => {
      dispatch(setInvitationFilters({ ...filters, page: value + ONE }));
    },
    [dispatch, filters]
  );

  const handleOnSearch = debounce(
    (value) =>
      dispatch(
        setInvitationFilters({
          ...filters,
          userName: value,
          organizationId,
          page: ONE,
        })
      ),
    SEARCH_DEBOUNCE_TIME
  );

  const resendInviteUser = (row) => {
    let userInvite = {
      email: row.email,
      invitedBy: row.invitedBy,
      name: row.username,
      organizationId: row.organizationId,
      profilePicUrl: EMPTY_STRING,
      role: row.role,
      sub: uuid(),
      applications: EMPTY_ARRAY,
    };
    dispatch(inviteUser(userInvite)).then(async (res) => {
      if (res.payload) {
        getInvitations();
      }
    });
  };

  const handleInviteNewUser = useCallback(() => {
    dispatch(setSelectedModal(MANAGE_USER_MODAL));
  }, [dispatch]);

  const statusFormatter = (row) => {
    let statusButtonText = EMPTY_STRING;
    if (row.status === PENDING && row.expireAt < currentDate)
      statusButtonText = EXPIRED;
    else statusButtonText = row.status;
    return (
      <div className="text-center">
        <span
          className={classNames({
            "text-yellow-300": statusButtonText === PENDING,
            "text-green-400": statusButtonText === ACCEPTED,
            "text-red-400": statusButtonText === EXPIRED,
          })}
        >
          <Spinner name={`${INVITE_USER}${row.email}`} persistSize>
            {statusButtonText}
            {statusButtonText === EXPIRED && (
              <span className="block">
                <span
                  className="text-blue-500 underline cursor-pointer"
                  onClick={() => resendInviteUser(row)}
                >
                  Resend Invite
                </span>
              </span>
            )}
          </Spinner>
        </span>
      </div>
    );
  };

  const roleFormatter = (row) => capitalizeFirstLetterOfWord(row.role);

  const dateFormatter = (row) =>
    row.expireAt ? getFormattedDate(row.expireAt) : "NOT_APPLICABLE";

  const columns = [
    {
      text: "UserName",
      dataField: "username",
    },
    {
      text: "Email",
      dataField: "email",
    },
    {
      text: "Status",
      formatter: statusFormatter,
    },
    {
      text: "Expiry",
      formatter: dateFormatter,
    },
    {
      text: "Role",
      formatter: roleFormatter,
    },
  ];

  const renderInvitations = () => {
    return invitationsData.length ? (
      <>
        <Table data={invitationsData} columns={columns} />
        <Pagination
          handlePagination={handlePagination}
          pagesCount={pageCount}
          activePage={filters.page - ONE}
          count={invitationsCount}
        />
      </>
    ) : (
      <EmptyLayout
        title="Welcome to Tava"
        subTitle="You can get started by inviting a new user."
        buttonText="Invite User"
        image={usersIcon}
        handleOnClick={handleInviteNewUser}
      />
    );
  };

  return (
    <div className="content overflow-auto flex-1">
      <Helmet>
        <title>Invitations | TavaJS</title>
      </Helmet>
      <div className="flex gap-6 flex-row items-center border-b border-gray-800 px-6 py-4 text-gray-400">
        <button
          className="rounded-lg py-2 px-4 hover:bg-gray-800 hover:text-gray-50 text-sm font-medium cursor-pointer"
          onClick={() => navigate(USERS)}
        >
          <span>Users</span>
        </button>
        <button className="rounded-lg py-2 px-4 text-sm font-medium bg-gray-800 text-gray-50">
          <span>Invitations</span>
        </button>
        <button
          className="rounded-lg py-2 px-4 hover:bg-gray-800 text-sm font-medium hover:text-white cursor-pointer"
          onClick={() => navigate(USERROLES)}
        >
          <span>User Roles</span>
        </button>
      </div>
      <div className="p-6">
        <div className="flex-col xs:flex-row flex justify-between mb-4 gap-4">
          {(!!invitationsData.length || filters.userName) && (
            <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} />
          )}
        </div>
      </div>
      <div className="h-2/3 p-6 -mt-[48px]">
        <Spinner name={FETCH_INVITATIONS}>{renderInvitations()}</Spinner>
      </div>
    </div>
  );
};
export default Invitations;
